import React, { useState, useReducer, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  InputBase,
  Typography,
  Grid,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { Link } from "react-router-dom";
import { BeatLoader } from "react-spinners";

import FadeInUp from "bundles/common/utils/FadeInUp";
import sh from "bundles/common/utils/sh";
import SubmitButton from "buttons/submit.svg";
import blueLogo from "images/blue-logo.svg";

const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
const MAX_ORIGINAL_BYTES = 30 * 1024 * 1024;
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/jpg", "image/pjpeg", "image/png", "image/webp", "image/gif"];

const loadImage = (file) => new Promise((resolve, reject) => {
  const image = new Image();
  const objectUrl = URL.createObjectURL(file);
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error("Could not read this photo."));
  };
  image.src = objectUrl;
});

const canvasToJpegFile = (image, maxDimension, quality, filename) => new Promise((resolve) => {
  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.width * scale));
  canvas.height = Math.max(1, Math.round(image.height * scale));
  const context = canvas.getContext("2d");
  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  canvas.toBlob((blob) => {
    if (!blob) {
      resolve(null);
      return;
    }
    const name = `${filename.replace(/\.[^.]+$/, "")}.jpg`;
    resolve(new File([blob], name, { type: "image/jpeg" }));
  }, "image/jpeg", quality);
});

const preparePhoto = async (file) => {
  if (file.size <= MAX_UPLOAD_BYTES) {
    return file;
  }

  if (file.type === "image/gif") {
    throw new Error("This GIF is too large to send. Please choose a JPEG, PNG, or WebP photo.");
  }

  const image = await loadImage(file);
  const attempts = [
    { maxDimension: 1920, quality: 0.88 },
    { maxDimension: 1600, quality: 0.8 },
    { maxDimension: 1280, quality: 0.72 },
    { maxDimension: 1024, quality: 0.65 },
  ];

  for (const attempt of attempts) {
    const compressed = await canvasToJpegFile(image, attempt.maxDimension, attempt.quality, file.name);
    if (compressed && compressed.size <= MAX_UPLOAD_BYTES) {
      return compressed;
    }
  }

  throw new Error("This photo is too large to send. Please try another one.");
};

const initialFormState = {
  values: {
    fullName: "",
    content: "",
  },
  loading: false,
};

const formReducer = (state, action) => {
  switch (action.type) {
  case "SET_VALUES":
    return {
      ...state,
      values: {
        ...state.values,
        [action.payload.name]: action.payload.value,
      },
    };
  case "SET_LOADING":
    return {
      ...state,
      loading: action.payload,
    };
  default:
    return state;
  }
};

const ShareReview = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const { enqueueSnackbar } = useSnackbar();
  const [state, dispatch] = useReducer(formReducer, initialFormState);
  const [submitHovered, setSubmitHovered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoPreparing, setPhotoPreparing] = useState(false);
  const [sendingStatus, setSendingStatus] = useState("");
  const { values, loading } = state;

  const buttonStyle = {
    textTransform: "none",
    color: theme.palette.secondary.main,
    fontSize: "1rem",
    margin: theme.spacing(1),
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch({ type: "SET_VALUES", payload: { name, value } });
  };

  const clearPhoto = () => {
    if (photoPreview) {
      URL.revokeObjectURL(photoPreview);
    }
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      enqueueSnackbar("Please upload a JPEG, PNG, WebP, or GIF photo.", { variant: "error" });
      return;
    }

    if (file.size > MAX_ORIGINAL_BYTES) {
      enqueueSnackbar("Please choose a smaller photo.", { variant: "error" });
      return;
    }

    setPhotoPreparing(true);
    try {
      const prepared = await preparePhoto(file);
      clearPhoto();
      setPhotoFile(prepared);
      setPhotoPreview(URL.createObjectURL(prepared));
    } catch (error) {
      enqueueSnackbar(error.message || "Could not process this photo.", { variant: "error" });
    } finally {
      setPhotoPreparing(false);
    }
  };

  const validateForm = (formValues) => {
    const errors = {};

    if (!formValues.fullName.trim()) {
      errors.fullName = "Full name is required.";
    }

    if (!formValues.content.trim()) {
      errors.content = "The review text is required.";
    }

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm(values);
    const isValid = Object.keys(formErrors).length === 0;

    if (!isValid) {
      enqueueSnackbar(Object.values(formErrors).join(" "), { variant: "error" });
      return;
    }

    if (photoPreparing) {
      enqueueSnackbar("Please wait while your picture is prepared.", { variant: "error" });
      return;
    }

    setIsSubmitting(true);
    dispatch({ type: "SET_LOADING", payload: true });

    const formData = new FormData();
    formData.append("authenticity_token", authenticityToken);
    formData.append("review_data[name]", values.fullName);
    formData.append("review_data[content]", values.content);
    if (photoFile) {
      formData.append("photo", photoFile);
    }

    sh.post("/reviews", formData, { timeout: 60000 })
      .then(() => {
        enqueueSnackbar("Thank you, your review has been submitted.", { variant: "success" });
        setTimeout(() => {
          setIsSubmitting(false);
          window.location.href = window.location.pathname;
        }, 1200);
      })
      .catch((error) => {
        const serverErrors = error.response?.data?.errors;
        const message = Array.isArray(serverErrors) && serverErrors.length > 0
          ? serverErrors.join(" ")
          : "Failed to submit review.";
        enqueueSnackbar(message, { variant: "error" });
        setIsSubmitting(false);
      })
      .finally(() => {
        dispatch({ type: "SET_LOADING", payload: false });
      });
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isSubmitting) {
      setSendingStatus("");
      return undefined;
    }

    setSendingStatus("Sending your review...");
    if (!photoFile) return undefined;

    const pictureTimer = setTimeout(() => {
      setSendingStatus("Sending your picture...");
    }, 1600);

    return () => clearTimeout(pictureTimer);
  }, [isSubmitting, photoFile]);

  return (
    <>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: isDesktop ? "60%" : "80%", mt: 1, mb: 2, mx: "auto" }}>
        <FadeInUp>
          <Typography
            variant="h2"
            style={{
              color: theme.palette.secondary.main,
              textAlign: "center",
              fontSize: "36px",
              paddingTop: "48px",
            }}
          >
            Share your <strong><em>review</em></strong>
          </Typography>
          <Typography
            variant="body1"
            style={{
              color: theme.palette.secondary.main,
              textAlign: "center",
              marginTop: "16px",
            }}
          >
            Thank you for taking the time to share your experience with HealthMission Africa.
            Tell us about the highlights of your stay and what you would like future participants to know.
          </Typography>
        </FadeInUp>
        <FadeInUp>
          <Grid container spacing={4} sx={{ mt: 2 }} justifyContent="center">
            <Grid item xs={12} sm={6}>
              <InputBase
                fullWidth
                placeholder="Full name*"
                name="fullName"
                value={values.fullName}
                onChange={handleChange}
                sx={{
                  borderBottom: `2px solid ${theme.palette.secondary.main}`,
                  color: theme.palette.secondary.main,
                  textAlign: "center",
                  mb: 2,
                  fontSize: "1rem !important",
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  border: `1px solid ${theme.palette.secondary.main}`,
                  borderRadius: "4px",
                  p: "8px",
                  mb: 2,
                }}
              >
                <InputBase
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Write your review*"
                  name="content"
                  value={values.content}
                  onChange={handleChange}
                  sx={{
                    color: theme.palette.secondary.main,
                    textAlign: "left",
                    width: "100%",
                    "& .MuiInputBase-inputMultiline": {
                      padding: 0,
                    },
                    fontSize: "1rem !important",
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ textAlign: "center", mb: 1 }}>
                <input
                  id="review-photo"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handlePhotoChange}
                  style={{ display: "none" }}
                />
                {photoPreparing ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                      border: `1px dashed ${theme.palette.secondary.main}`,
                      borderRadius: "4px",
                      p: 3,
                    }}
                  >
                    <BeatLoader size={8} color="#173571" />
                    <Typography sx={{ color: theme.palette.secondary.main, fontSize: "1rem !important" }}>
                      Preparing your picture...
                    </Typography>
                  </Box>
                ) : photoPreview ? (
                  <Box>
                    <Box
                      component="img"
                      src={photoPreview}
                      alt="Selected review photo"
                      sx={{
                        maxHeight: "180px",
                        maxWidth: "100%",
                        borderRadius: "8px",
                        objectFit: "cover",
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: theme.palette.secondary.main, mt: 1, fontSize: "0.95rem !important" }}
                    >
                      {photoFile?.name}
                    </Typography>
                    <Button
                      type="button"
                      onClick={clearPhoto}
                      sx={{
                        textTransform: "none",
                        color: theme.palette.secondary.main,
                        mt: 0.5,
                      }}
                    >
                      Remove photo
                    </Button>
                  </Box>
                ) : (
                  <Box
                    component="label"
                    htmlFor="review-photo"
                    sx={{
                      display: "block",
                      border: `1px dashed ${theme.palette.secondary.main}`,
                      borderRadius: "4px",
                      p: 3,
                      cursor: "pointer",
                      color: theme.palette.secondary.main,
                      "&:hover": {
                        backgroundColor: "rgba(23, 53, 113, 0.04)",
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: "1rem !important", color: theme.palette.secondary.main }}>
                      Add a picture representative of your experience
                    </Typography>
                    <Typography sx={{ fontSize: "0.85rem !important", color: theme.palette.secondary.main, mt: 0.5 }}>
                      JPEG, PNG or WebP.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box textAlign="center" mt={2}>
                {isSubmitting ?
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1.5,
                    }}
                  >
                    <BeatLoader size={10} color="#173571" />
                    <Typography
                      sx={{
                        color: theme.palette.secondary.main,
                        fontSize: "1rem !important",
                      }}
                    >
                      {sendingStatus}
                    </Typography>
                  </Box>
                  :
                  <img
                    src={SubmitButton}
                    alt="Submit"
                    onClick={!loading && !photoPreparing ? handleSubmit : undefined}
                    style={{
                      cursor: "pointer",
                      width: "160px",
                      transform: submitHovered ? "scale(1.1)" : "scale(1)",
                      transition: "all 0.3s ease",
                      opacity: loading || photoPreparing ? 0.5 : 1,
                      pointerEvents: loading || photoPreparing ? "none" : "auto",
                    }}
                    onMouseEnter={() => setSubmitHovered(true)}
                    onMouseLeave={() => setSubmitHovered(false)}
                  />
                }
              </Box>
            </Grid>
          </Grid>
        </FadeInUp>
      </Box>
      <Box sx={{ width: "100%", bgcolor: "#F8F8F8", p: 2 }}>
        <Grid container justifyContent="center" alignItems="center">
          <FadeInUp>
            <Button component="a" href={`/${i18nLocale}/apply`} rel="noopener noreferrer" sx={buttonStyle}>
              Join Us
            </Button>
          </FadeInUp>
          <FadeInUp>
            <Button component="a" href={`/${i18nLocale}/about_us`} rel="noopener noreferrer" sx={buttonStyle}>
              About
            </Button>
          </FadeInUp>
          <FadeInUp>
            <Button component="a" href="https://www.linkedin.com/company/healthmission-africa/" target="_blank" rel="noopener noreferrer" sx={buttonStyle}>
              Linkedin
            </Button>
          </FadeInUp>
          <FadeInUp>
            <Button component="a" href="https://www.instagram.com/healthmissionafrica/" target="_blank" rel="noopener noreferrer" sx={buttonStyle}>
              Instagram
            </Button>
          </FadeInUp>
          <div style={{ display: "flex" }}>
            <FadeInUp>
              <Link to={`/${i18nLocale}`} style={{ textDecoration: "none", color: "inherit" }}>
                <img src={blueLogo} alt="HealthMission Africa Logo" style={{ height: "50px", marginLeft: isDesktop ? theme.spacing(80) : theme.spacing(2) }} />
              </Link>
            </FadeInUp>
          </div>
        </Grid>
      </Box>
    </>
  );
};

export default ShareReview;
